import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import VisorComprobante from 'shared/components/VisorComprobante';
import { FiFilter, FiEye, FiCheck, FiX, FiArrowRight } from 'react-icons/fi';
import { C } from 'shared/darkColors';

const ESTADOS_LABEL = {
  pendiente: 'Pendiente',
  aplicado: 'Aplicada',
  rechazado: 'Rechazada',
  cancelado: 'Cancelada',
};

function estadoBadge(estado) {
  switch (estado) {
    case 'aplicado':
      return C.badge('#ecfdf5', '#059669');
    case 'pendiente':
      return C.badge('#fffbeb', '#d97706');
    case 'rechazado':
      return C.badge('#fef2f2', '#dc2626');
    default:
      return C.badge('#f3f4f6', '#6b7280');
  }
}

function tipoTag(tipo) {
  switch (tipo) {
    case 'upgrade':
      return C.tag('#eff6ff', '#2563eb');
    case 'downgrade':
      return C.tag('#fff7ed', '#c2410c');
    default:
      return C.tag('#f3f4f6', '#6b7280');
  }
}

export default function SolicitudesCambio() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('pendiente');
  const [visorUrl, setVisorUrl] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filtro ? `?estado=${filtro}` : '';
      const r = await API.get(`/super/solicitudes-cambio${params}`);
      if (r.data.status === 'success') setSolicitudes(r.data.data);
    } catch (e) {
      setError('Error al cargar solicitudes');
      console.error('Error cargando solicitudes:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => {
    load();
  }, [load]);

  const abrirConfirm = (tipo, s) => {
    setMotivo('');
    setConfirm({ tipo, s });
  };
  const cerrarConfirm = () => {
    if (!procesando) {
      setConfirm(null);
      setMotivo('');
    }
  };

  const ejecutar = async () => {
    if (!confirm) return;
    const { tipo, s } = confirm;
    if (tipo === 'rechazar' && !motivo.trim()) return;
    setProcesando(true);
    try {
      if (tipo === 'aprobar') {
        await API.put(`/super/solicitud-cambio/${s.id}/aprobar`);
        showToast('Cambio de plan aplicado.');
      } else {
        await API.put(`/super/solicitud-cambio/${s.id}/rechazar`, null, {
          params: { motivo: motivo.trim() },
        });
        showToast('Solicitud rechazada.');
      }
      setConfirm(null);
      setMotivo('');
      load();
    } catch (e) {
      showToast(e.response?.data?.detail || 'Error al procesar la solicitud', false);
    } finally {
      setProcesando(false);
    }
  };

  const sedesActuales = (s) =>
    (s.metadata && (s.metadata.sede_count ?? s.metadata.sedes_actuales)) ?? '--';

  if (loading) return <div style={C.loading}>Cargando...</div>;

  if (error)
    return (
      <div style={C.wrapper}>
        <div style={{ ...C.section, textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: '8px' }}>{error}</div>
          <button onClick={load} style={C.btnOutline}>
            Reintentar
          </button>
        </div>
      </div>
    );

  return (
    <div className="solc-wrapper" style={C.wrapper}>
      <style>{`
        @media (max-width: 768px) {
          .solc-wrapper { padding: 16px 12px !important; }
          .solc-card { overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 18px !important; border-radius: 12px !important; }
          .solc-card table { min-width: 900px; }
          .solc-filter { flex-wrap: wrap; }
          .solc-filter select { flex: 1; min-width: 160px; }
        }
      `}</style>
      {visorUrl && <VisorComprobante comprobanteUrl={visorUrl} onClose={() => setVisorUrl(null)} />}
      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#059669' : '#dc2626' }}>{toast.msg}</div>
      )}

      <div style={C.topBar}>
        <div style={C.title}>Cambios de plan</div>
        <div style={C.subtitle}>Solicitudes de cambio de plan / sedes de todas las empresas</div>
      </div>

      <div className="solc-card" style={C.card}>
        <div className="solc-filter" style={C.filterBar}>
          <FiFilter size={16} color="#9ca3af" />
          <select value={filtro} onChange={(e) => setFiltro(e.target.value)} style={C.select}>
            <option value="pendiente">Pendientes</option>
            <option value="aplicado">Aplicadas</option>
            <option value="rechazado">Rechazadas</option>
            <option value="">Todas</option>
          </select>
          <div style={C.count}>{solicitudes.length} solicitudes</div>
        </div>

        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Fecha</th>
              <th style={C.th}>Empresa</th>
              <th style={C.th}>Plan</th>
              <th style={C.th}>Sedes</th>
              <th style={C.th}>Monto</th>
              <th style={C.th}>Comprobante</th>
              <th style={C.th}>Estado</th>
              <th style={C.th}>Accion</th>
            </tr>
          </thead>
          <tbody>
            {solicitudes.length === 0 ? (
              <tr>
                <td colSpan={8} style={C.emptyRow}>
                  No hay solicitudes
                </td>
              </tr>
            ) : (
              solicitudes.map((s) => (
                <tr key={s.id}>
                  <td style={C.td}>
                    {s.creado ? new Date(s.creado + 'Z').toLocaleDateString() : '--'}
                  </td>
                  <td style={{ ...C.td, fontWeight: 600 }}>{s.empresa_nombre || '--'}</td>
                  <td style={C.td}>
                    <span style={C.pill}>
                      <span>{s.plan_actual_nombre || '--'}</span>
                      <FiArrowRight size={12} color="#9ca3af" />
                      <strong>{s.plan_nuevo_nombre || '--'}</strong>
                      <span style={tipoTag(s.tipo_cambio)}>{s.tipo_cambio}</span>
                    </span>
                  </td>
                  <td style={C.td}>
                    <span style={C.pill}>
                      {sedesActuales(s)} <FiArrowRight size={12} color="#9ca3af" />{' '}
                      <strong>{s.sedes_objetivo}</strong>
                    </span>
                  </td>
                  <td
                    style={{
                      ...C.td,
                      fontWeight: 700,
                      color: s.monto_usd > 0 ? '#059669' : '#6b7280',
                    }}
                  >
                    {s.monto_usd > 0 ? `$${s.monto_usd}` : 'Gratis'}
                  </td>
                  <td style={C.td}>
                    {s.comprobante_url ? (
                      <button onClick={() => setVisorUrl(s.comprobante_url)} style={C.btnVer}>
                        <FiEye size={12} />
                        Ver
                      </button>
                    ) : (
                      '--'
                    )}
                  </td>
                  <td style={C.td}>
                    <span style={estadoBadge(s.estado)}>{ESTADOS_LABEL[s.estado] || s.estado}</span>
                  </td>
                  <td style={C.td}>
                    {s.estado === 'pendiente' && (
                      <div style={C.actions}>
                        <button
                          onClick={() => abrirConfirm('aprobar', s)}
                          style={{
                            ...C.btnSm,
                            background: '#ecfdf5',
                            color: '#059669',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <FiCheck size={12} />
                          Aprobar
                        </button>
                        <button
                          onClick={() => abrirConfirm('rechazar', s)}
                          style={{
                            ...C.btnSm,
                            background: '#fef2f2',
                            color: '#dc2626',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <FiX size={12} />
                          Rechazar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirm && (
        <div style={C.overlay} onClick={cerrarConfirm}>
          <div style={C.modal} onClick={(e) => e.stopPropagation()}>
            <div style={C.modalTitle}>
              {confirm.tipo === 'aprobar' ? 'Aprobar cambio de plan' : 'Rechazar solicitud'}
            </div>
            <div style={C.modalDesc}>
              {confirm.tipo === 'aprobar'
                ? 'Se aplicará el cambio de plan a la empresa de inmediato (se cancela la suscripción actual, se crea la nueva y se sincroniza el gating). Esta acción no se puede deshacer.'
                : 'La solicitud quedara rechazada y el pago asociado (si existe) se marcara como rechazado.'}
            </div>

            <div style={C.resumen}>
              <div style={C.resRow}>
                <span style={C.resLabel}>Empresa</span>
                <span style={C.resVal}>{confirm.s.empresa_nombre}</span>
              </div>
              <div style={C.resRow}>
                <span style={C.resLabel}>Plan</span>
                <span style={C.resVal}>
                  {confirm.s.plan_actual_nombre || '--'} → {confirm.s.plan_nuevo_nombre}
                </span>
              </div>
              <div style={C.resRow}>
                <span style={C.resLabel}>Sedes</span>
                <span style={C.resVal}>
                  {sedesActuales(confirm.s)} → {confirm.s.sedes_objetivo}
                </span>
              </div>
              <div style={C.resRow}>
                <span style={C.resLabel}>Monto</span>
                <span style={C.resVal}>
                  {confirm.s.monto_usd > 0 ? `$${confirm.s.monto_usd}` : 'Gratis'}
                </span>
              </div>
            </div>

            {confirm.tipo === 'rechazar' && (
              <div>
                <label style={C.motivoLabel}>Motivo del rechazo *</label>
                <textarea
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  style={C.motivoInput}
                  placeholder="Ej: el comprobante no corresponde al monto."
                />
              </div>
            )}

            <div style={C.modalActions}>
              <button onClick={cerrarConfirm} disabled={procesando} style={C.btnCancel}>
                Cancelar
              </button>
              {confirm.tipo === 'aprobar' ? (
                <button
                  onClick={ejecutar}
                  disabled={procesando}
                  style={{ ...C.btnAprobar, opacity: procesando ? 0.6 : 1 }}
                >
                  <FiCheck size={14} />
                  {procesando ? 'Aprobando...' : 'Aprobar'}
                </button>
              ) : (
                <button
                  onClick={ejecutar}
                  disabled={procesando || !motivo.trim()}
                  style={{ ...C.btnRechazar, opacity: procesando || !motivo.trim() ? 0.6 : 1 }}
                >
                  <FiX size={14} />
                  {procesando ? 'Rechazando...' : 'Rechazar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
